import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';
import { LucideIcon } from 'lucide-react';

interface TaskStatusCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: LucideIcon;
  trend?: {
    data: Array<{ name: string; value: number }>;
    description: string;
  };
  details?: {
    description: string;
    items: Array<{ label: string; value: string }>;
  };
}

export function TaskStatusCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  details,
}: TaskStatusCardProps) {
  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{value}</span>
              <Badge variant="secondary" className="font-normal">
                {description}
              </Badge>
            </div>
          </div>
          <div className="rounded-full bg-primary/10 p-3">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>

        {trend && (
          <div className="h-[40px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend.data}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground mt-2">
              {trend.description}
            </p>
          </div>
        )}

        {details && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-3">
              {details.description}
            </p>
            <div className="grid grid-cols-2 gap-4">
              {details.items.map((item, index) => (
                <div key={index} className="space-y-1">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-sm font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}